import { useMemo } from 'react'
import { syncraApi, syncraApiAdapter } from '@/api/main/syncraClient'
import type { ApiError, TopupsReferralListParamsStatusEnum, TraderTopupProfileListResultR, TraderTopupProfile } from 'syncra-api-client'
import { DEFAULT_TAKE } from '@/shared/constants/constants'
import { normalizeDateRangeForList } from '@/shared/lib/date'
import { DateRange } from 'react-day-picker'

type UseReferralTopupsParams = {
  referralId: string
  status: TopupsReferralListParamsStatusEnum
  skip?: number
  take?: number
  dateRange?: DateRange
  search?: string
}

type UseReferralTopupsResult = {
  data: {
    items: TraderTopupProfile[] | null
    count: number
    effectiveSkip: number
  }
  loading: boolean
  error: ApiError | null
}

export function useReferralTopups(params: UseReferralTopupsParams): UseReferralTopupsResult {
  const effectiveSkip = params.skip ?? 0
  const take = params.take ?? DEFAULT_TAKE
  
  // Нормализуем даты для API запроса
  const { startDate, endDate } = normalizeDateRangeForList(params.dateRange)

  const query = useMemo(() => {
    const queryParams: any = {
      referralId: params.referralId,
      status: params.status,
      skip: effectiveSkip,
      take,
    }
    // Добавляем поисковый запрос если есть
    if (params.search && params.search.trim()) {
      queryParams.search = params.search.trim()
    }
    // Добавляем даты если они есть (API может принимать их, даже если не в типах)
    if (startDate) {
      queryParams.startDate = startDate
    }
    if (endDate) {
      queryParams.endDate = endDate
    }
    return queryParams
  }, [params.referralId, params.status, effectiveSkip, take, params.search, startDate, endDate])

  const queryKey = useMemo(() => [
    'referral-topups',
    params.referralId,
    params.status,
    effectiveSkip,
    take,
    params.search,
    startDate,
    endDate,
  ], [params.referralId, params.status, effectiveSkip, take, params.search, startDate, endDate])

  const { data: rawData, isLoading: loading, error } = syncraApiAdapter.createQueryHook<TraderTopupProfileListResultR>(
    queryKey,
    () => syncraApi.v1.topupsReferralList(query)
  )()


  const items = useMemo(() => {
    // TraderTopupProfileListResultR имеет структуру { value?: TraderTopupProfileListResult }
    // Проверяем оба варианта доступа к данным
    if (!rawData) return null
    const resultData = (rawData as any)?.value ?? rawData
    return resultData?.items ?? null
  }, [rawData])

  const count = useMemo(() => {
    if (!rawData) return 0
    const resultData = (rawData as any)?.value ?? rawData
    return resultData?.count ?? 0
  }, [rawData])

  return {
    data: {
      items,
      count,
      effectiveSkip,
    },
    loading,
    error: error as ApiError | null,
  }
}

